import {
  IconCalendar,
  IconCornerBottom,
  IPopoverProps,
  Popover2 as Popover,
  Button,
} from '@wheely/ui-kit';
import { createContext, useCallback, useMemo, useState } from 'react';

import { noop } from '@/shared/utils/noop';

export type ExtractProperty<T, K extends keyof T> = Required<T>[K];

export type WeekSelectorProps = {
  children: JSX.Element;
  buttonTitle?: string;
  disabled?: boolean;
  usePortalForPopover?: boolean;
  popoverClassName?: string;
};

const defaultContext = {
  isOpen: false,
  openPopover: noop,
  closePopover: noop,
};

type TWeekSelectorContext = typeof defaultContext;

const WeekSelectorContext = createContext<TWeekSelectorContext>(defaultContext);

const WeekSelector = ({
  children,
  buttonTitle,
  disabled,
  usePortalForPopover = true,
  popoverClassName,
}: WeekSelectorProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const handlePopoverOpening = useCallback(() => {
    setIsOpen(true);
  }, []);

  const handlePopoverClosed = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handlePopoverInteraction = useCallback<ExtractProperty<IPopoverProps, 'onInteraction'>>(
    nextOpenState => {
      setIsOpen(nextOpenState);
    },
    [],
  );

  const contextValue = useMemo(
    () => ({
      isOpen,
      openPopover: handlePopoverOpening,
      closePopover: handlePopoverClosed,
    }),
    [isOpen, handlePopoverOpening, handlePopoverClosed],
  );

  return (
    <WeekSelectorContext.Provider value={contextValue}>
      <Popover
        className={popoverClassName}
        minimal={true}
        placement="bottom-start"
        onOpening={handlePopoverOpening}
        onClosed={handlePopoverClosed}
        onInteraction={handlePopoverInteraction}
        openOnTargetFocus={true}
        isOpen={isOpen}
        disabled={disabled}
        modifiers={{
          offset: {
            enabled: true,
            options: { offset: [0, 8] },
          },
        }}
        content={children}
        usePortal={usePortalForPopover}
      >
        <Button icon={<IconCalendar />} rightIcon={<IconCornerBottom />}>
          {buttonTitle}
        </Button>
      </Popover>
    </WeekSelectorContext.Provider>
  );
};

export { WeekSelector, WeekSelectorContext };
